import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings, digits } from "@/context/SettingsContext";
import { CheckCircle2, MessageCircle } from "lucide-react";

const STATUSES = [
  "order_confirmed","processing","packed","shipped","out_for_delivery","delivered","cancelled","return_requested","returned","refunded"
];

export default function AdminOrders() {
  const { settings } = useSettings();
  const WHATSAPP = digits(settings.whatsapp_number);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const load = () => api.get("/admin/orders").then(({data}) => setOrders(data));
  useEffect(() => { load(); }, []);

  const save = async (o) => {
    await api.patch(`/admin/orders/${o.order_number}`, {
      status: o.status,
      courier: o.tracking?.courier,
      tracking_number: o.tracking?.tracking_number,
      tracking_url: o.tracking?.tracking_url,
    });
    toast.success("Order updated");
    setEditing(null); load();
  };

  const verifyPayment = async (orderNumber) => {
    if (!window.confirm("Confirm the UPI payment has been received in your bank account?")) return;
    try {
      await api.patch(`/admin/orders/${orderNumber}/verify-payment`);
      toast.success("Payment marked as PAID");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not verify");
    }
  };

  const badge = (status) => {
    if (status === "paid") return <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">PAID</span>;
    if (status === "awaiting_verification") return <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Awaiting</span>;
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground capitalize">{(status||"pending").replace(/_/g," ")}</span>;
  };

  return (
    <div className="space-y-4" data-testid="admin-orders">
      <h1 className="font-serif text-4xl">Orders</h1>
      <div className="border hairline rounded-xl bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="p-3">Order</th><th>Date</th><th>Customer</th><th>Amount</th><th>Payment</th><th>UTR</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {orders.map((o) => {
              const isEdit = editing?.order_number === o.order_number;
              const cur = isEdit ? editing : o;
              const isOpen = expanded === o.order_number;
              return (
                <React.Fragment key={o.order_number}>
                  <tr className="border-t hairline align-top">
                    <td className="p-3 font-medium">
                      <button onClick={() => setExpanded(isOpen ? null : o.order_number)} className="hover:gold-text" data-testid={`toggle-${o.order_number}`}>{o.order_number}</button>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()}</td>
                    <td className="p-3">
                      <div>{o.address?.full_name}</div>
                      <div className="text-xs text-muted-foreground">{o.address?.phone}</div>
                    </td>
                    <td className="p-3">₹{o.total}</td>
                    <td className="p-3">{badge(o.payment_status)}</td>
                    <td className="p-3 text-xs font-mono">{o.utr_reference || "—"}</td>
                    <td className="p-3">
                      {isEdit ? (
                        <select value={cur.status} onChange={(e)=>setEditing({...cur, status:e.target.value})} className="border hairline rounded px-2 py-1 text-xs">
                          {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
                        </select>
                      ) : <span className="capitalize gold-text">{o.status.replace(/_/g, " ")}</span>}
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {o.payment_status !== "paid" && (
                          <button onClick={() => verifyPayment(o.order_number)} className="text-[11px] bg-emerald-600 text-white rounded px-2 py-1 flex items-center gap-1 hover:bg-emerald-700" data-testid={`verify-${o.order_number}`}>
                            <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> Verify Payment
                          </button>
                        )}
                        {isEdit ? (
                          <>
                            <input placeholder="Courier" value={cur.tracking?.courier || ""} onChange={(e)=>setEditing({...cur, tracking:{...cur.tracking, courier:e.target.value}})} className="border hairline rounded px-2 py-1 text-xs w-32" />
                            <input placeholder="Tracking #" value={cur.tracking?.tracking_number || ""} onChange={(e)=>setEditing({...cur, tracking:{...cur.tracking, tracking_number:e.target.value}})} className="border hairline rounded px-2 py-1 text-xs w-32" />
                            <input placeholder="Tracking URL" value={cur.tracking?.tracking_url || ""} onChange={(e)=>setEditing({...cur, tracking:{...cur.tracking, tracking_url:e.target.value}})} className="border hairline rounded px-2 py-1 text-xs w-40" />
                            <div className="flex gap-1">
                              <button onClick={() => save(cur)} className="btn-gold rounded px-3 py-1 text-xs">Save</button>
                              <button onClick={() => setEditing(null)} className="text-xs text-muted-foreground">Cancel</button>
                            </div>
                          </>
                        ) : (
                          <button className="text-[11px] gold-text" onClick={() => setEditing(o)} data-testid={`edit-${o.order_number}`}>Update status</button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-t hairline bg-muted/30">
                      <td colSpan={8} className="p-4">
                        <div className="grid md:grid-cols-3 gap-4 text-xs">
                          <div>
                            <div className="uppercase tracking-widest text-muted-foreground mb-2">Delivery</div>
                            <div>{o.address?.full_name}</div>
                            <div>{o.address?.phone}</div>
                            <div className="text-muted-foreground">{o.address?.line1}{o.address?.line2 ? ", " + o.address.line2 : ""}</div>
                            <div className="text-muted-foreground">{o.address?.city}, {o.address?.state} — {o.address?.pincode}</div>
                            <div className="text-muted-foreground">{o.address?.country}</div>
                          </div>
                          <div>
                            <div className="uppercase tracking-widest text-muted-foreground mb-2">Items</div>
                            {o.items?.map((it) => (
                              <div key={it.product_id} className="flex items-center gap-2 mb-1">
                                <img src={it.image} className="w-8 h-8 rounded object-cover" alt="" />
                                <div className="flex-1">{it.name} ×{it.quantity}</div>
                                <div>₹{it.line_total}</div>
                              </div>
                            ))}
                          </div>
                          <div>
                            <div className="uppercase tracking-widest text-muted-foreground mb-2">Payment</div>
                            <div>Method: {o.payment_method?.toUpperCase()}</div>
                            <div>Status: {o.payment_status}</div>
                            <div>UTR: <span className="font-mono">{o.utr_reference || "—"}</span></div>
                            {o.payment_verified_at && <div className="text-emerald-700">Verified at {new Date(o.payment_verified_at).toLocaleString()}</div>}
                            {WHATSAPP && <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`AdiYogi Handcrafts order ${o.order_number} update`)}`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-emerald-700 hover:underline"><MessageCircle className="w-3 h-3" strokeWidth={1.5}/> Message customer</a>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
